import { NativePurchases, PURCHASE_TYPE, type Product, type Transaction } from "@capgo/native-purchases";
import { api, ApiError } from "./api";
import { nativePlatform } from "./native";
import { APPLE_PRODUCTS, GOOGLE_PRODUCT } from "@shared/billing";
import type { BillingDTO, BillingPlan, StoreProvider } from "@shared/types";

export const storeProvider = (): StoreProvider => (nativePlatform() === "ios" ? "apple" : "google");
export const storeName = (provider: StoreProvider = storeProvider()) => (provider === "apple" ? "App Store" : "Google Play");

export interface StorePlan {
  plan: BillingPlan;
  /** Localized by the store, e.g. "$39.99". Apple requires showing the store's price. */
  price: string;
  /** A free trial this store account can still get. */
  trialDays: number | null;
  productId: string;
  /** Android only: the offer to buy. */
  offerToken?: string;
}

const PLANS: BillingPlan[] = ["annual", "quarterly"];

function trialDaysOf(product: Product): number | null {
  const intro = product.introductoryPrice;
  if (!intro || intro.price !== 0) return null;
  const { unitString, numberOfUnits } = intro.subscriptionPeriod ?? {};
  const days = { day: 1, week: 7, month: 30, year: 365 }[unitString as string];
  return days && numberOfUnits ? days * numberOfUnits : null;
}

/** What the store sells this account. Throws if the store can't be reached. */
export async function loadStorePlans(): Promise<StorePlan[]> {
  if (storeProvider() === "apple") {
    const { products } = await NativePurchases.getProducts({ productIdentifiers: Object.values(APPLE_PRODUCTS), productType: PURCHASE_TYPE.SUBS });
    return PLANS.flatMap((plan) => {
      const product = products.find((p) => p.identifier === APPLE_PRODUCTS[plan]);
      return product ? [{ plan, price: product.priceString, trialDays: trialDaysOf(product), productId: product.identifier }] : [];
    });
  }
  const { products } = await NativePurchases.getProducts({ productIdentifiers: [GOOGLE_PRODUCT], productType: PURCHASE_TYPE.SUBS });
  return PLANS.flatMap((plan) => {
    // Google lists one entry per eligible offer: a trial offer comes before the plain base plan.
    const offers = products.filter((p) => p.planIdentifier === plan).sort((a, b) => Number(trialDaysOf(b) !== null) - Number(trialDaysOf(a) !== null));
    const offer = offers[0];
    return offer ? [{ plan, price: offer.priceString, trialDays: trialDaysOf(offer), productId: GOOGLE_PRODUCT, offerToken: offer.offerToken }] : [];
  });
}

const proofOf = (t: Transaction) => (storeProvider() === "apple" ? t.jwsRepresentation : t.purchaseToken);

async function report(t: Transaction): Promise<BillingDTO> {
  const proof = proofOf(t);
  if (!proof) throw new ApiError(0, "The store didn't return a receipt. Please try Restore purchases.", "store_no_proof");
  const billing = await api.post<BillingDTO>("/billing/store/verify", { provider: storeProvider(), proof });
  // Only after the server has it: an unfinished purchase is offered again the next time.
  if (storeProvider() === "apple") await NativePurchases.finishTransaction({ transactionId: t.transactionId }).catch(() => {});
  return billing;
}

/** The person closed the store's sheet: not an error worth showing. */
export const purchaseCancelled = (err: unknown) => /cancel/i.test((err as Error)?.message ?? "");

export async function buyStorePlan(plan: StorePlan, accountToken: string): Promise<BillingDTO> {
  const transaction = await NativePurchases.purchaseProduct({
    productIdentifier: plan.productId,
    planIdentifier: storeProvider() === "google" ? plan.plan : undefined,
    offerToken: plan.offerToken,
    productType: PURCHASE_TYPE.SUBS,
    appAccountToken: accountToken,
    autoAcknowledgePurchases: false,
  });
  return report(transaction);
}

/**
 * Report the store account's subscriptions to the server. Also recovers a purchase that the app
 * couldn't report at the time. Returns the latest billing state, or null if there was nothing to report.
 */
export async function reportStorePurchases(options: { prompt?: boolean } = {}): Promise<BillingDTO | null> {
  if (options.prompt) await NativePurchases.restorePurchases();
  const { purchases } = await NativePurchases.getPurchases({ productType: PURCHASE_TYPE.SUBS, onlyCurrentEntitlements: true });
  let latest: BillingDTO | null = null;
  let failure: unknown = null;
  for (const purchase of purchases) {
    try {
      latest = await report(purchase);
    } catch (err) {
      failure = err;
    }
  }
  if (!latest && failure) throw failure;
  return latest;
}

export const manageStoreSubscription = () => NativePurchases.manageSubscriptions();
